import { createStyled } from '@lib/style/createStyled';
import { prop } from '@lib/style/theme';
import { unitStrict } from '@lib/style/unit';
import { classList } from '@lib/utils';
import Icon from '@material-ui/core/Icon';
import Typography from '@material-ui/core/Typography';
import { font } from '@packages/style/fonts';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { Row, RowProps } from './Layout/Row';

const Background = ({ backgroundColor, bg }) => backgroundColor || bg || '#f5f5f5';

const VARIANTS = {
    root: {
        contained: () => css`
            background-color: ${Background};
        `,

        boxed: () => css`
            background-color: ${Background};
            border: ${({ border, bd }: any) => border || bd || '1px solid #d0d0d0'};
            border-radius: ${({ borderRadius }: any) => borderRadius || '0px'};
        `,
    },
};

export interface NothingToShowProps extends RowProps {
    title?: any;
    className?: string;
    variant?: 'base' | 'boxed' | 'contained';
    children?: any;
    icon?: any;
    text?: any;
    maxTextWidth?: number | string;
}

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: NothingToShow
 * @date:  24.03.2019
 * @time:  01:27
 * @scope:
 */
export function NothingToShow(props: NothingToShowProps) {
    const { variant, title, children, icon, maxTextWidth, text, ...rest } = props;

    rest.className = classList(props.className, `variant--${variant || 'base'}`);

    let children$ = children;
    let text$ = text;

    if (!text$) {
        if (children$) {
            text$ = children$;
            children$ = null;
        }
    }

    return (
        <Style align={'center'} justify={'center'} layout={'column'} padding={'52px'} {...rest}>
            {icon ? (
                <IconHolder>
                    {React.isValidElement(icon) ? (
                        icon
                    ) : (
                        <Icon fontSize={'large'} classes={{ root: 'icon-element' }}>
                            {icon}
                        </Icon>
                    )}
                </IconHolder>
            ) : null}

            {title ? React.isValidElement(title) ? title : <Title>{title}</Title> : null}

            {text$ ? (
                React.isValidElement(text$) ? (
                    text$
                ) : (
                    <Text variant={'body1'} color={'textSecondary'} display={'inline'} maxWidth={maxTextWidth}>
                        {text$}
                    </Text>
                )
            ) : null}

            {children$ ? <CustomContent>{children$}</CustomContent> : null}
        </Style>
    );
}

NothingToShow.displayName = 'NothingToShow';

NothingToShow.renderNoData = (data: any): any => {
    if (!data) {
        return null;
    }

    if (React.isValidElement(data)) {
        return data;
    }

    if (typeof data === 'object' && !data.$$typeof) {
        return <NothingToShow {...data} />;
    }

    const Comp = data;
    return <Comp />;
};

const variant =
    (component, variant$) =>
    ({ ...props }) => {
        if (!VARIANTS[component]) {
            throw new Error(`Undefined component set: 'VARIANTS.${component}'`);
        }

        if (!variant$) {
            return '';
        }

        const variantHandler = VARIANTS[component][variant$];

        if (typeof variantHandler === 'function') {
            // $FlowFixMe
            return variantHandler({ ...props });
        }

        return variantHandler;
    };

const IconHolder = styled.div`
    & {
        margin-top: 14px;
        margin-bottom: 8px;
        display: flex;
        width: 100%;
        align-items: center;
        align-content: center;
        justify-content: center;
        &,
        * {
            color: #707070;
        }
        .icon-element {
            width: 48px;
            height: 48px;
            line-height: 48px;
            font-size: 48px;
        }
    }
`;

const Title = styled.h3`
    & {
        text-align: center;
        color: #222;
        margin: 0 0 8px;
        ${font(14, 500)}
    }
`;

const Text = createStyled(
    Typography,
    'maxWidth',
    ({ css }) => css`
        & {
            text-align: center;
            max-width: ${prop('maxWidth', 300, unitStrict)};
            color: #999;
            font-size: 12px !important;
        }
    `,
);

const CustomContent = styled.div`
    & {
        margin-top: 14px;
    }
`;

const Style = styled(({ backgroundColor, bg, border, bd, ...rest }) => <Row {...rest} />)`
    && {
        position: relative;
        &.variant {
            &--boxed {
                ${variant('root', 'boxed')}
            }
            &--contained {
                ${variant('root', 'contained')}
            }
        }
    }
`;

export const NothingToShowStyle = Style;
